import { useEffect, useMemo, useState } from "react";
import { Package, Diamond, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { loadRazorpayScript, openRazorpayCheckout, UPI_ONLY_CONFIG } from "@/lib/razorpay";

export default function ResellerStocks() {
  const { reseller, refresh } = useAuth();
  const [config, setConfig] = useState([]);
  const [platform, setPlatform] = useState("trumeet");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const loadConfig = () => api.get("/reseller/stock/config").then(({ data }) => {
    const list = data.platforms || [];
    setConfig(list);
    if (list.length && !list.find((p) => p.key === platform)) setPlatform(list[0].key);
  }).catch(() => {});
  const loadOrders = () => api.get("/reseller/stock/orders").then(({ data }) => setOrders(data.orders || [])).catch(() => {});

  useEffect(() => { loadConfig(); loadOrders(); }, []);

  const current = useMemo(() => config.find((p) => p.key === platform) || {}, [config, platform]);
  const amt = parseFloat(amount) || 0;
  const price = current.price || 0;
  const diamonds = price > 0 ? Math.round(amt / price) : 0;

  const handleBuy = async () => {
    if (amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (diamonds <= 0) { toast.error("Amount too small for the current price"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/reseller/stock/initiate", { platform, amount_inr: amt });
      const finalise = async (rsp) => {
        try {
          const { data: v } = await api.post("/reseller/stock/verify", {
            order_id: data.order_id,
            razorpay_order_id: rsp.razorpay_order_id,
            razorpay_payment_id: rsp.razorpay_payment_id,
            razorpay_signature: rsp.razorpay_signature,
          });
          toast.success(`Payment received! ${diamonds.toLocaleString()} 💎 pending admin approval.`);
          setAmount("");
          loadOrders();
          refresh();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally { setLoading(false); }
      };
      if (data.razorpay.mock) {
        toast.message("Razorpay not configured — using mock checkout.");
        await finalise({ razorpay_order_id: data.razorpay.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: `mock_${Date.now()}` });
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Razorpay failed to load"); setLoading(false); return; }
      openRazorpayCheckout({
        key: data.razorpay.key_id,
        amount: data.razorpay.amount,
        currency: data.razorpay.currency,
        order_id: data.razorpay.order_id,
        name: "Eagle Eyes Services",
        description: `${current.name} diamond stock refill`,
        prefill: { email: reseller?.email, name: reseller?.name || "", contact: reseller?.phone || "" },
        config: UPI_ONLY_CONFIG,
        theme: { color: "#18181b" },
        handler: finalise,
        modal: { ondismiss: () => setLoading(false) },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reseller-stocks-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Stock</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Refill Stock</h1>
        <p className="text-zinc-500 mt-1">Pay via Razorpay to top up your allocation. Delivery is credited after admin approval.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Platform</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="stock-platform-selector">
              {config.map((p) => (
                <button key={p.key} type="button" onClick={() => setPlatform(p.key)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${platform === p.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
                  data-testid={`stock-platform-${p.key}`}>{p.name}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Amount to pay (₹)</Label>
            <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 5000" data-testid="stock-amount-input" />
            <p className="text-xs text-zinc-500 mt-1">Price: ₹{Number(price).toFixed(2)} per diamond</p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between" data-testid="stock-preview-box">
            <div className="text-sm text-emerald-900">Stock you'll receive <span className="text-[11px] text-emerald-700">(after approval)</span></div>
            <div className="font-display text-xl font-semibold text-emerald-900 tabular-nums font-mono" data-testid="stock-preview-value">{diamonds.toLocaleString()} 💎</div>
          </div>

          <Button onClick={handleBuy} disabled={loading || diamonds <= 0} className="bg-zinc-900 hover:bg-zinc-800" data-testid="stock-pay-button">
            <Package className="w-4 h-4 mr-2" /> {loading ? "Processing…" : `Pay ₹${amt.toLocaleString()}`}
          </Button>
        </div>

        <div className="bg-zinc-950 text-white rounded-lg p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">{current.name || "Current"} Balance</div>
          <div className="flex items-baseline gap-2">
            <Diamond className="w-6 h-6 text-zinc-400" />
            <div className="font-display text-4xl font-semibold tabular-nums font-mono" data-testid="stock-current-balance">{(current.balance ?? 0).toLocaleString()}</div>
          </div>
          <p className="mt-4 text-xs text-zinc-400">Your paid stock orders appear in the Payments tab. Diamonds are credited here once an admin approves the order.</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg" data-testid="stock-recent-orders">
        <div className="p-6 border-b border-zinc-100">
          <h2 className="font-display text-lg font-medium text-zinc-900">Recent stock orders</h2>
        </div>
        <div className="divide-y divide-zinc-100">
          {orders.slice(0, 8).map((o) => (
            <div key={o.id} className="flex items-center justify-between px-6 py-3 text-sm" data-testid={`stock-order-${o.id}`}>
              <div>
                <div className="font-medium text-zinc-900">{o.platform_name} · {(o.diamonds ?? 0).toLocaleString()} 💎</div>
                <div className="text-xs text-zinc-500">₹{o.amount_inr} · {new Date(o.created_at).toLocaleString()}</div>
              </div>
              <StatusPill status={o.status} />
            </div>
          ))}
          {orders.length === 0 && <div className="px-6 py-10 text-center text-sm text-zinc-400">No stock orders yet.</div>}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "bg-amber-100 text-amber-800", i: Clock, t: "Pending approval" },
    approved: { c: "bg-green-100 text-green-800", i: CheckCircle2, t: "Approved" },
    rejected: { c: "bg-red-100 text-red-800", i: XCircle, t: "Rejected" },
    created: { c: "bg-zinc-100 text-zinc-600", i: Clock, t: "Awaiting payment" },
    failed: { c: "bg-red-100 text-red-800", i: XCircle, t: "Failed" },
  };
  const m = map[status] || map.pending;
  const Icon = m.i;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${m.c}`}><Icon className="w-3 h-3" /> {m.t}</span>;
}
